import { useEffect, useRef, useState } from "react";
import { Send, Bot, Headphones, User } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const fmtTime = (iso, lang) => (iso ? new Date(iso).toLocaleTimeString(lang === "en" ? "en-AE" : "ar-AE-u-nu-latn", { hour: "2-digit", minute: "2-digit" }) : "");

export const RichText = ({ text }) => (
  <>{String(text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : p))}</>
);

const AI_NAME = { ar: "مساعد بازارو الذكي", en: "Bazaroo AI Assistant" };

function Bubble({ m, name, lang }) {
  const cust = m.sender_role === "user";
  const Icon = m.sender_role === "ai" ? Bot : cust ? User : Headphones;
  return (
    <div dir="ltr" className={`flex items-end gap-2 ${cust ? "justify-start" : "justify-end"}`} data-testid={`support-msg-${m.sender_role}`}>
      {cust && <span className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-slate-600" /></span>}
      <div className={`max-w-[75%] flex flex-col ${cust ? "items-start" : "items-end"}`}>
        {name && <p dir="auto" className={`text-[11px] font-semibold mb-1 px-1 ${cust ? "text-slate-500" : m.sender_role === "ai" ? "text-[#0F3D26]" : "text-[#3A8F5C]"}`} data-testid="support-msg-sender-name">{name}</p>}
        <div dir="auto" className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${cust ? "bg-[#ECEDEF] text-slate-800 rounded-bl-sm" : m.sender_role === "ai" ? "bg-[#0F3D26] text-white rounded-br-sm" : "bg-[#3A8F5C] text-white rounded-br-sm"}`}>
          <RichText text={m.text} />
          <p className={`text-[10px] mt-1 ${cust ? "text-slate-400" : "text-white/70"}`}>{fmtTime(m.created_at, lang)}</p>
        </div>
      </div>
      {!cust && <span className="w-8 h-8 rounded-full bg-[#E8EFEA] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#1A5C3A]" /></span>}
    </div>
  );
}

export function ChatThread({ messages, customerName, testid = "support-thread" }) {
  const { lang } = useLang();
  const end = useRef(null);
  useEffect(() => { const box = end.current?.parentElement; if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" }); }, [messages.length]);
  const nameOf = (m) => (m.sender_role === "ai" ? AI_NAME[lang] || AI_NAME.ar : m.sender_name || (m.sender_role === "user" ? customerName : ""));
  return (
    <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-[#FAF8F3]" data-testid={testid}>
      {messages.map((m, i) => (m.sender_role === "system"
        ? <p key={m.msg_id || i} className="text-center text-xs text-slate-500 whitespace-pre-wrap bg-white/70 rounded-xl px-3 py-2 mx-auto max-w-md" data-testid="support-msg-system">{(lang === "en" && m.text_en) || m.text}</p>
        : <Bubble key={m.msg_id || i} m={m} name={nameOf(m)} lang={lang} />))}
      <div ref={end} />
    </div>
  );
}

export function ChatComposer({ onSend, disabled, placeholder, testid = "support-input" }) {
  const [text, setText] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const v = text.trim();
    if (!v) return;
    setText("");
    Promise.resolve(onSend(v)).then((ok) => { if (!ok) setText((t) => t || v); });
  };
  return (
    <form onSubmit={submit} className="flex gap-2 p-3 border-t border-[#EFEBE0] bg-white">
      <input value={text} onChange={(e) => setText(e.target.value)} disabled={disabled} maxLength={2000} placeholder={placeholder}
        className="flex-1 h-11 rounded-full border border-[#EFEBE0] px-4 outline-none focus:border-[#1A5C3A] disabled:bg-slate-50" data-testid={testid} />
      <button type="submit" disabled={disabled || !text.trim()} className="w-11 h-11 rounded-full bazaro-gradient text-white flex items-center justify-center disabled:opacity-50" data-testid={`${testid}-send`}>
        <Send className="w-4 h-4 rtl:-scale-x-100" />
      </button>
    </form>
  );
}
